import { useEffect, useRef, type ElementType, type ReactNode } from "react";

/**
 * Shared observer so many Reveal mounts don't each read layout.
 * IntersectionObserver reports visibility asynchronously after the browser's
 * own layout pass — no getBoundingClientRect / offsetWidth forced reflow.
 */
let revealObserver: IntersectionObserver | null = null;
const observed = new Set<Element>();

function getRevealObserver() {
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          if (entry.isIntersecting) {
            el.classList.add("reveal-in");
            revealObserver?.unobserve(el);
            observed.delete(el);
          } else {
            el.classList.add("reveal");
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
  }
  return revealObserver;
}

function observeReveal(el: Element) {
  getRevealObserver().observe(el);
  observed.add(el);
  return () => {
    revealObserver?.unobserve(el);
    observed.delete(el);
    if (observed.size === 0) {
      revealObserver?.disconnect();
      revealObserver = null;
    }
  };
}

export function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li" | "article" | "header";
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    return observeReveal(el);
  }, []);

  const Comp = Tag as ElementType;
  return (
    <Comp ref={ref} style={{ transitionDelay: `${delay}ms` }} className={className}>
      {children}
    </Comp>
  );
}
